import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { budgetService } from '../../../services/apiService';

const BudgetActual = () => {
  const { id } = useParams();
  const [budget, setBudget] = useState(null);
  const [actualSpending, setActualSpending] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch budget details
        const budgetData = await budgetService.getBudgetById(id);
        setBudget(budgetData);
        
        // Fetch actual spending
        const actualData = await budgetService.getActualSpending(id);
        setActualSpending(actualData);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load budget data');
        toast.error('Failed to load budget comparison data');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  if (loading) {
    return <div className="flex justify-center p-8">Loading budget data...</div>;
  }

  if (error || !budget) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          <p>{error || 'Budget not found'}</p>
          <Link to="/budgeting" className="text-blue-500 hover:underline mt-2 inline-block">
            Back to Budgets
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  // Calculate percentage of budget used
  const calculatePercentage = (actual, planned) => {
    if (planned === 0) return 0;
    return (actual / planned) * 100;
  };

  // Determine color based on budget usage
  const getProgressColor = (percentage) => {
    if (percentage <= 75) return 'bg-green-500';
    if (percentage <= 90) return 'bg-yellow-500';
    if (percentage <= 100) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="p-4">
      <div className="flex items-center mb-6">
        <Link to={`/budgeting/${budget._id}`} className="text-blue-500 hover:text-blue-700 mr-4">
          <FaArrowLeft className="inline mr-1" /> Back to Budget
        </Link>
        <h1 className="text-2xl font-semibold text-gray-800">{budget.name}: Actual vs Budget</h1>
      </div>
      
      <div className="mb-4 text-gray-600">
        <p>Period: {formatDate(budget.period.startDate)} - {formatDate(budget.period.endDate)}</p>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-blue-700">Planned Expenses</h3>
          <p className="text-2xl font-bold text-gray-800">{formatCurrency(budget.totalPlannedExpenses)}</p>
        </div>
        <div className="bg-red-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-red-700">Actual Expenses</h3>
          <p className="text-2xl font-bold text-gray-800">{formatCurrency(budget.totalActualExpenses)}</p>
        </div>
        <div className={`p-4 rounded-lg ${budget.totalPlannedExpenses >= budget.totalActualExpenses ? 'bg-green-50' : 'bg-red-50'}`}>
          <h3 className="text-lg font-medium text-gray-800">Difference</h3>
          <p className={`text-2xl font-bold ${budget.totalPlannedExpenses >= budget.totalActualExpenses ? 'text-green-700' : 'text-red-700'}`}>
            {formatCurrency(budget.totalPlannedExpenses - budget.totalActualExpenses)}
          </p>
        </div>
      </div>
      
      {/* Categories Breakdown */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">Category Breakdown</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-4 text-left text-gray-700 font-semibold">Category</th>
                <th className="p-4 text-right text-gray-700 font-semibold">Planned</th>
                <th className="p-4 text-right text-gray-700 font-semibold">Actual</th>
                <th className="p-4 text-right text-gray-700 font-semibold">Difference</th>
                <th className="p-4 text-right text-gray-700 font-semibold">% Used</th>
                <th className="p-4 text-left text-gray-700 font-semibold">Progress</th>
              </tr>
            </thead>
            <tbody>
              {budget.categories.map((category) => {
                const categoryId = category.categoryId?.toString() || '';
                const actualCategory = actualSpending[categoryId] || { totalAmount: 0 };
                const actualAmount = actualCategory.totalAmount || 0;
                const difference = category.plannedAmount - actualAmount;
                const percentage = calculatePercentage(actualAmount, category.plannedAmount);
                
                return (
                  <tr key={category._id} className="border-b hover:bg-gray-50">
                    <td className="p-4 text-gray-800 font-medium">{category.name}</td>
                    <td className="p-4 text-right text-gray-800">
                      {formatCurrency(category.plannedAmount)}
                    </td>
                    <td className="p-4 text-right text-gray-800">
                      {formatCurrency(actualAmount)}
                    </td>
                    <td className="p-4 text-right">
                      <span className={difference >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {formatCurrency(difference)}
                      </span>
                    </td>
                    <td className="p-4 text-right text-gray-800">
                      {percentage.toFixed(1)}%
                    </td>
                    <td className="p-4">
                      <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${getProgressColor(percentage)}`}
                          style={{ width: `${Math.min(percentage, 100)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Transactions Section (can be expanded to show actual transactions) */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">Recent Transactions</h2>
        </div>
        
        {Object.keys(actualSpending).length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">No transactions recorded for this budget period.</p>
          </div>
        ) : (
          <div className="p-4">
            {Object.entries(actualSpending).map(([categoryId, data]) => {
              const category = budget.categories.find(c => c.categoryId?.toString() === categoryId);
              
              if (!category || !data.transactions || data.transactions.length === 0) return null;
              
              return (
                <div key={categoryId} className="mb-6">
                  <h3 className="text-lg font-medium mb-2">{category.name}</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="p-2 text-left text-gray-700 font-semibold">Date</th>
                          <th className="p-2 text-left text-gray-700 font-semibold">Description</th>
                          <th className="p-2 text-right text-gray-700 font-semibold">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.transactions.slice(0, 5).map((transaction) => (
                          <tr key={transaction._id} className="border-b hover:bg-gray-50">
                            <td className="p-2 text-gray-800">
                              {formatDate(transaction.date)}
                            </td>
                            <td className="p-2 text-gray-800">
                              {transaction.description}
                            </td>
                            <td className="p-2 text-right text-gray-800">
                              {formatCurrency(transaction.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetActual; 